import { yupResolver } from '@hookform/resolvers/yup';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Button } from '@src/common/components/button/Button';
import { FilterableTable } from '@src/common/components/FilterableTable';
import type { Filter } from '@src/common/components/Filters';
import { NumberInput } from '@src/common/components/NumberInput';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { partition } from '@src/common/utils/partition';
import { AddPermitsForm } from '@src/orders/AddPermitsForm';
import type { Order } from '@src/orders/domain/Order';
import { OrderActions } from '@src/orders/domain/OrderList';
import type { PermitTypeList } from '@src/orders/domain/PermitType';
import { useAddPermits } from '@src/orders/hooks/useAddPermits';
import { usePermitTypes } from '@src/orders/hooks/usePermitTypes';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import type { PermitTypeFilters } from '@src/orders/services/PermitTypesClient';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import type { Control, SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import type { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

export const AddOrderPermits = ({ order }: { order: Order }) => {
  const { t } = useTranslation();
  const { filteredPermitTypes, permitTypes, filters } = usePermitTypes();

  return (
    <PageLayout title={`${t('addPermitsForOrder')} #${order.displayId}`}>
      {filteredPermitTypes && (
        <PermitTable
          order={order}
          filteredPermitTypes={filteredPermitTypes}
          permitTypes={permitTypes}
          filters={filters}
        />
      )}
    </PageLayout>
  );
};

interface Props {
  order: Order;
  filteredPermitTypes: PermitTypeList;
  permitTypes: PermitTypeList;
  filters: {
    filters: Filter<PermitTypeFilters>[];
    onChange: (filters: PermitTypeFilters) => void;
  };
}

const PermitTable = ({
  order,
  filteredPermitTypes,
  permitTypes,
  filters: { onChange, filters },
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const preservedEditValues = location.state?.preservedValues;
  const { refreshOrder } = useRefreshOrder({ id: order.id });
  const isReadonly =
    !order.canDo(OrderActions.ADD_PERMIT) &&
    !order.canDo(OrderActions.CHANGE_REQUEST);

  const orderPermitCountPerPermitType = order.getLatestPermitQuantityMatching(
    permitTypes.permitTypes,
  );

  const initialSelectionOrder = orderPermitCountPerPermitType
    .filter((p) => p.quantity > 0)
    .map((p) => p.type.id);

  const [selectionOrder, setSelectionOrder] = useState<string[]>(
    initialSelectionOrder,
  );

  const { register, control, handleSubmit, watch } = useForm({
    resolver: yupResolver(AddPermitsForm),
    defaultValues: {
      permitSelections: permitTypes.permitTypes.map((permitType) => ({
        quantity:
          orderPermitCountPerPermitType.find(
            (permit) => permit.type.originalId === permitType.originalId,
          )?.quantity || 0,
        typeId: permitType.id,
        typeOriginalId: permitType.originalId,
      })),
    },
    disabled: isReadonly,
  });

  const navigateToOrderForm = () => {
    refreshOrder();
    navigate(OrdersNavigation.edit.url(order.id), {
      state: {
        preservedValues: preservedEditValues,
        fromAddPermits: true,
      },
    });
  };

  const { addPermits, isLoading: addingPermits } = useAddPermits({
    orderId: order.id,
    onSuccess: navigateToOrderForm,
  });

  const watchedPermitSelections = watch('permitSelections');
  const selectionWithQuantity = watchedPermitSelections.filter(
    (selection) => selection.quantity > 0,
  );

  const handleQuantityChange = (
    typeId: string,
    oldQuantity: number,
    newQuantity: number,
    onChange: (value: number) => void,
  ) => {
    onChange(newQuantity);

    if (oldQuantity === 0 && newQuantity > 0) {
      setSelectionOrder((prevOrder) => [...prevOrder, typeId]);
    } else if (oldQuantity > 0 && newQuantity === 0) {
      setSelectionOrder((prevOrder) => prevOrder.filter((id) => id !== typeId));
    }
  };

  const hasAtLeastOnePermit = selectionWithQuantity.length > 0;
  const orderHasPermitApplication = order.permitApplicationGroups.length !== 0;

  const submitSelection: SubmitHandler<AddPermitsForm> = () => {
    const orderedPermits = [...selectionWithQuantity].sort(
      (a, b) =>
        selectionOrder.indexOf(a.typeId) - selectionOrder.indexOf(b.typeId),
    );
    addPermits({ permits: orderedPermits });
  };

  const allRows = permitTypes.permitTypes
    .filter((p) => filteredPermitTypes.permitTypes.some((t) => t.id === p.id))
    .map((permitType) => ({
      permitType,
      orderPermit: orderPermitCountPerPermitType.find(
        (op) => op.type.id === permitType.id,
      ),
    }))
    .filter(
      ({ permitType, orderPermit }) =>
        permitType.isActive || (orderPermit?.quantity ?? 0) > 0,
    )
    .map(({ permitType, orderPermit }) => ({
      id: permitType.id,
      name: permitType.name,
      type: permitType.type,
      jurisdiction: permitType.jurisdiction,
      count: orderHasPermitApplication
        ? orderPermit?.quantity.toString()
        : undefined,
      maxQuantity: permitType.isActive ? undefined : orderPermit?.quantity,
    }));

  const selectedIds = selectionWithQuantity.map((s) => s.typeId);
  const [selectedRows, remainingRows] = partition(allRows, (row) =>
    selectedIds.includes(row.id),
  );

  const sortedSelectedRows = [...selectedRows].sort(
    (a, b) => selectionOrder.indexOf(a.id) - selectionOrder.indexOf(b.id),
  );

  const columns = generateColumns(
    t,
    orderHasPermitApplication,
    register,
    control,
    permitTypes,
    order,
    handleQuantityChange,
  );

  return (
    <form onSubmit={handleSubmit(submitSelection)}>
      <Stack direction="column" gap={theme.spacing['3']}>
        <FilterableTable
          columns={columns}
          rows={remainingRows}
          pinnedRows={{
            rows: sortedSelectedRows,
            collapsibleLabel: t('permitCollapsible', {
              count: sortedSelectedRows.length,
            }),
          }}
          ariaLabel="simple table"
          paginationAndSortMode="client"
          filters={{
            filters,
            onChange,
          }}
        />

        {!isReadonly ? (
          <Button
            disabled={addingPermits || !hasAtLeastOnePermit}
            type="submit"
          >
            {t('next')}
          </Button>
        ) : (
          <Button disabled={!hasAtLeastOnePermit} onClick={navigateToOrderForm}>
            {t('next')}
          </Button>
        )}
      </Stack>
    </form>
  );
};

const generateColumns = (
  t: TFunction,
  orderHasPermitApplication: boolean,
  register: UseFormRegister<AddPermitsForm>,
  control: Control<AddPermitsForm>,
  permitsList: PermitTypeList,
  order: Order,
  handleQuantityChange: (
    typeId: string,
    oldQuantity: number,
    newQuantity: number,
    onChange: (value: number) => void,
  ) => void,
) => {
  const columns: GridColDef[] = [
    { field: 'name', headerName: t('permitType.name'), flex: 1 },
    { field: 'type', headerName: t('permitType.type'), flex: 1 },
    {
      field: 'jurisdiction',
      headerName: t('permitType.jurisdiction'),
      flex: 1,
    },
  ];
  if (orderHasPermitApplication) {
    columns.push({
      field: 'count',
      headerName: t('permit.currentCount'),
      minWidth: 150,
      flex: 0,
    });
  }
  columns.push({
    field: 'add',
    headerName: t('add'),
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    hideable: false,
    flex: 0,
    minWidth: 200,
    renderCell: (params) => {
      const indexInFullList = permitsList.permitTypes.findIndex(
        (permit) => permit.id === params.row.id,
      );
      return (
        <Stack
          direction="row"
          paddingTop={theme.spacing['1']}
          paddingBottom={theme.spacing['1']}
          //Without this line 'onClick' events are being stopped. Forcing the user to click once to focus the input/cell
          //Then click again to be able to increment/decrement properly
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            type="hidden"
            {...register(`permitSelections.${indexInFullList}.typeId`)}
            value={params.row.id}
          />
          <Controller
            name={`permitSelections.${indexInFullList}.quantity`}
            control={control}
            render={({ field, formState: { disabled } }) => (
              <NumberInput
                defaultValue={field.value}
                min={
                  order.canDo(OrderActions.REMOVE_PERMITS)
                    ? 0
                    : params.row.count
                }
                max={params.row.maxQuantity}
                onChange={(_, newValue) =>
                  handleQuantityChange(
                    params.row.id,
                    field.value,
                    newValue ?? 0,
                    field.onChange,
                  )
                }
                disabled={disabled}
              />
            )}
          />
        </Stack>
      );
    },
  });
  return columns;
};
